import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import { Message, toaster } from "rsuite"
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    LinearProgress,
    MenuItem,
    TextField
} from "@mui/material"
import {
    Download,
    GroupsOutlined,
    RefreshOutlined,
    SchoolOutlined,
    Search,
    SearchOff
} from "@mui/icons-material"

// Still imported: the Class Students and Admit Card pages share some of its
// global class names (class-card, section-heading, students-empty, ...).
import "./RegisteredSudents.css"

import { GetCurrentUser } from "../utils/hooks"
import { Loader } from "../components/index"
import EmptyState from "../Lectures/components/EmptyState"
import DocViewer from "./components/ViewDoc"
import ClassList from "../components/ClassList"
import StudentList from "./registered/StudentList"
import {
    GENDER_OPTIONS,
    STATUS_TABS,
    buildAdmitPayload,
    fullName,
    getStatusKey,
    matchesSearch,
    toDownloadRows
} from "./registered/helpers"

import {
    useCreateClassStudentMutation,
    useLazyGetClassStudentsQuery
} from "../Redux/actions/studentAction"
import {
    useLazyGetAllinstitutionRegisteredStudentsQuery,
    useUpdateRegistrationStatusMutation
} from "../Redux/actions/admissionSetup.action"
import { useLazyGetInstitutionClassesQuery } from "../Redux/actions/classSetup.action"
import { useDownloadExcelFileMutation } from "../Redux/actions/download.action"

const toast = (type, message, placement = "topCenter") =>
    toaster.push(<Message type={type}>{message}</Message>, { placement })

export default function RegisteredStudents() {
    const [activeClassId, setActiveClassId] = useState(null)
    const [statusTab, setStatusTab] = useState("all")
    const [gender, setGender] = useState("all")
    const [search, setSearch] = useState("")
    const [busyId, setBusyId] = useState(null)
    const [admitTarget, setAdmitTarget] = useState(null)
    const [docsStudent, setDocsStudent] = useState(null)

    const [getClasses, classesState] = useLazyGetInstitutionClassesQuery()
    const [getRegisteredStudents, registeredState] = useLazyGetAllinstitutionRegisteredStudentsQuery()
    const [getClassStudents] = useLazyGetClassStudentsQuery()
    const [createStudent] = useCreateClassStudentMutation()
    const [updateStatus] = useUpdateRegistrationStatusMutation()
    const [downloadStudents, downloadState] = useDownloadExcelFileMutation()

    /* ------------------------------ data ------------------------------ */

    const classes = useMemo(() => {
        const list = _.get(classesState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [classesState])

    const activeClass = useMemo(
        () => classes.find((item) => item.id === activeClassId) || null,
        [classes, activeClassId]
    )

    const students = useMemo(() => {
        if (!activeClass) return []
        const list = _.get(registeredState, "currentData.list", [])
        return Array.isArray(list) ? list : []
    }, [activeClass, registeredState])

    const statusCounts = useMemo(
        () => _.countBy(students, getStatusKey),
        [students]
    )

    const visibleStudents = useMemo(
        () =>
            students.filter(
                (student) =>
                    (statusTab === "all" || getStatusKey(student) === statusTab) &&
                    (gender === "all" || String(student?.gender || "").toLowerCase() === gender) &&
                    matchesSearch(student, search)
            ),
        [students, statusTab, gender, search]
    )

    const isFiltered = statusTab !== "all" || gender !== "all" || search.trim() !== ""

    /* ----------------------------- fetching ---------------------------- */

    const fetchClasses = () => {
        const institution_ref = _.get(GetCurrentUser(), "meta_data.user_platform", null)
        if (institution_ref) getClasses({ institution_ref })
    }

    const fetchStudents = (item = activeClass) => {
        if (!item?.institution_ref || !item?.id) return
        getRegisteredStudents({ institution_ref: item.institution_ref, class_ref: item.id })
    }

    useEffect(() => {
        fetchClasses()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const selectClass = (item) => {
        setActiveClassId(item.id)
        setStatusTab("all")
        setGender("all")
        setSearch("")
        fetchStudents(item)
    }

    // Open the first class once classes arrive, or if the open one disappears
    useEffect(() => {
        if (classes.length > 0 && !activeClass) selectClass(classes[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes])

    const refresh = () => {
        fetchClasses()
        fetchStudents()
    }

    /* ------------------------------ actions ----------------------------- */

    const handleHold = async (student) => {
        const currentStatus = _.get(student, "meta_data.status", null)
        if (currentStatus === "hold") {
            toast("info", "Registration status is already hold.", "bottomCenter")
            return
        }
        if (currentStatus === "admit") {
            toast("warning", "This student has already been admitted into the class.", "bottomCenter")
            return
        }

        setBusyId(student.id)
        try {
            await updateStatus({
                registration_ref: student.id,
                meta_data: { ..._.get(student, "meta_data", {}), status: "hold" }
            }).unwrap()
            toast("success", "Registration status updated successfully!")
            fetchStudents()
        } catch {
            toast("error", "Error while updating registration status")
        } finally {
            setBusyId(null)
        }
    }

    const admit = async (student) => {
        setBusyId(student.id)
        try {
            // Count the class afresh so the new roll number is never stale
            let rollNo
            try {
                const classStudents = await getClassStudents({
                    class_id: student.class_ref,
                    school_id: student.institution_ref
                }).unwrap()
                rollNo = _.get(classStudents, "list", []).length + 1
            } catch {
                toast("error", "Couldn't load the class to assign a roll number. Please try again.")
                return
            }

            try {
                const created = await createStudent(buildAdmitPayload(student, rollNo)).unwrap()
                toast("success", _.get(created, "message", "Student admitted into class successfully!"))
            } catch (error) {
                toast("error", _.get(error, "data.detail", "Error while admitting the student into class"))
                return
            }

            try {
                await updateStatus({
                    registration_ref: student.id,
                    meta_data: { ..._.get(student, "meta_data", {}), status: "admit" }
                }).unwrap()
                toast("success", "Registration status updated successfully!")
            } catch {
                toast("error", "Error while updating registration status")
            }
            fetchStudents()
        } finally {
            setBusyId(null)
        }
    }

    const confirmAdmit = () => {
        const student = admitTarget
        setAdmitTarget(null)
        if (student) admit(student)
    }

    const handleDownload = async () => {
        if (visibleStudents.length === 0) {
            toast("info", "No students available to download.")
            return
        }
        try {
            const data = await downloadStudents(toDownloadRows(visibleStudents)).unwrap()
            const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.download = activeClass
                ? `${activeClass.class_name}-registered-students.xlsx`
                : "students.xlsx"
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch {
            toast("error", "Couldn't download the student list. Please try again.")
        }
    }

    const clearFilters = () => {
        setStatusTab("all")
        setGender("all")
        setSearch("")
    }

    /* ------------------------------- view ------------------------------- */

    const loadingClasses = classesState.isLoading || classesState.isFetching
    const loadingStudents = registeredState.isFetching

    const renderStudents = () => {
        if (registeredState.isError && !loadingStudents) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="Couldn't load registrations"
                        description="Please check your connection and try again."
                        actionLabel="Retry"
                        onAction={() => fetchStudents()}
                    />
                </div>
            )
        }

        if (loadingStudents && students.length === 0) {
            return (
                <div className="space-y-3 p-5">
                    {[0, 1, 2, 3, 4].map((key) => (
                        <div key={key} className="h-12 animate-pulse rounded-lg bg-slate-100" />
                    ))}
                </div>
            )
        }

        if (students.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="No registered students"
                        description="There are currently no student registrations for this class."
                    />
                </div>
            )
        }

        if (visibleStudents.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<SearchOff />}
                        title="No students match"
                        description="Try a different search or clear the filters."
                        actionLabel="Clear filters"
                        onAction={clearFilters}
                    />
                </div>
            )
        }

        return (
            <StudentList
                students={visibleStudents}
                busyId={busyId}
                onAdmit={setAdmitTarget}
                onHold={handleHold}
                onViewDocs={setDocsStudent}
            />
        )
    }

    return (
        <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 pt-6 sm:px-6 lg:px-8">

            {/* Page header */}
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
                        Admissions
                    </p>
                    <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[28px]">
                        Registered Students
                    </h1>
                    <p className="mt-1 max-w-xl text-sm text-slate-500">
                        Review registrations, put applicants on hold and admit students into their classes.
                    </p>
                </div>
                <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<RefreshOutlined />}
                    onClick={refresh}
                    disabled={loadingClasses || loadingStudents}
                    sx={{ textTransform: "none", borderRadius: "10px", borderColor: "#e2e8f0", color: "#334155" }}
                >
                    Refresh
                </Button>
            </header>

            {!loadingClasses && classes.length === 0 ? (
                <EmptyState
                    icon={<SchoolOutlined />}
                    title={classesState.isError ? "Couldn't load classes" : "No classes found"}
                    description={
                        classesState.isError
                            ? "Please check your connection and try again."
                            : "Create a class first to start managing registered students."
                    }
                    actionLabel={classesState.isError ? "Retry" : undefined}
                    onAction={classesState.isError ? fetchClasses : undefined}
                />
            ) : (
                <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">

                    {/* Classes */}
                    <aside className="lg:sticky lg:top-4 lg:self-start">
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-slate-700">Classes</h2>
                            <span className="text-xs tabular-nums text-slate-400">{classes.length}</span>
                        </div>
                        <ClassList
                            classes={classes}
                            activeId={activeClassId}
                            onSelect={selectClass}
                            loading={loadingClasses}
                        />
                    </aside>

                    {/* Students of the selected class */}
                    <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="h-1">
                            {loadingStudents && <LinearProgress sx={{ height: 3 }} />}
                        </div>

                        <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4 sm:px-5">
                            <div className="min-w-0">
                                <h2 className="truncate text-lg font-semibold text-slate-900">
                                    {activeClass?.class_name || "Select a class"}
                                </h2>
                                <p className="text-sm text-slate-500">
                                    {students.length} registered student{students.length === 1 ? "" : "s"}
                                </p>
                            </div>
                            <Button
                                variant="contained"
                                disableElevation
                                startIcon={<Download />}
                                onClick={handleDownload}
                                disabled={downloadState.isLoading || visibleStudents.length === 0}
                                sx={{ textTransform: "none", borderRadius: "10px" }}
                            >
                                {downloadState.isLoading
                                    ? "Preparing…"
                                    : isFiltered
                                        ? `Download ${visibleStudents.length}`
                                        : "Download"}
                            </Button>
                        </div>

                        {/* Status tabs double as the summary counts */}
                        <div
                            role="tablist"
                            aria-label="Filter by status"
                            className="mt-4 flex gap-1 overflow-x-auto border-b border-slate-200 px-4 sm:px-5"
                        >
                            {STATUS_TABS.map((tab) => {
                                const count = tab.key === "all" ? students.length : statusCounts[tab.key] || 0
                                const active = statusTab === tab.key
                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => setStatusTab(tab.key)}
                                        className={[
                                            "-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm transition-colors",
                                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500",
                                            active
                                                ? "border-indigo-600 font-semibold text-indigo-700"
                                                : "border-transparent text-slate-500 hover:text-slate-800"
                                        ].join(" ")}
                                    >
                                        {tab.label}
                                        <span
                                            className={[
                                                "rounded-full px-2 py-0.5 text-xs tabular-nums",
                                                active ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"
                                            ].join(" ")}
                                        >
                                            {count}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>

                        {/* Search and gender */}
                        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:px-5">
                            <TextField
                                size="small"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, email or phone"
                                inputProps={{ "aria-label": "Search students" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                            />
                            <TextField
                                select
                                size="small"
                                value={gender}
                                onChange={(e) => setGender(e.target.value)}
                                inputProps={{ "aria-label": "Filter by gender" }}
                                sx={{ minWidth: { sm: 170 }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                            >
                                {GENDER_OPTIONS.map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>

                        {renderStudents()}
                    </section>
                </div>
            )}

            {/* Admitting creates the student's class account, so confirm first */}
            <Dialog open={Boolean(admitTarget)} onClose={() => setAdmitTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Admit {fullName(admitTarget)}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        They will be added to {activeClass?.class_name || "the class"} with the next roll
                        number, and can sign in using their date of birth as the password.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAdmitTarget(null)} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        disableElevation
                        onClick={confirmAdmit}
                        sx={{ textTransform: "none" }}
                    >
                        Admit student
                    </Button>
                </DialogActions>
            </Dialog>

            <DocViewer
                isOpen={Boolean(docsStudent)}
                onClose={() => setDocsStudent(null)}
                data={docsStudent}
            />

            {classesState.isLoading && <Loader show={true} />}
        </div>
    )
}
